/** True when HTTP layer reports async queue ack (execute, close, etc.) — not a fill/close. */
export function isTradeResponseQueued(data: {
  queued?: boolean;
  duplicate?: boolean;
  success?: boolean;
  details?: { queued?: boolean; duplicate?: boolean; [key: string]: unknown } | null;
} | null | undefined): boolean {
  if (!data) return false;
  if (data.queued === true || data.duplicate === true) return true;
  const d = data.details;
  if (d && typeof d === 'object') {
    return d.queued === true || d.duplicate === true;
  }
  return false;
}

function sanitizeApiNote(raw: unknown, fallback: string): string {
  const note = String(raw || fallback)
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return note || fallback;
}

/** Network / missing HTTP layer — never labelled "queued". */
function isNetworkStatus(status: number): boolean {
  return status === 0;
}

export function interpretPositionCloseApiResponse(
  ok: boolean,
  status: number,
  data: {
    success?: boolean;
    queued?: boolean;
    duplicate?: boolean;
    error?: string;
    message?: string;
  } | null
  | undefined,
): { success: boolean; error?: string; unauthorized?: boolean } {
  if (status === 401 || status === 403) {
    return {
      success: false,
      unauthorized: status === 401,
      error: data?.error || data?.message || `HTTP ${status}`,
    };
  }

  const queued = isTradeResponseQueued(data);
  if (ok && data?.success === true && !queued) {
    return { success: true };
  }

  if (isNetworkStatus(status)) {
    return {
      success: false,
      error: 'Network error — failed to close position',
    };
  }

  const fallback = queued
    ? 'Close queued — position not closed yet'
    : 'Failed to close position';
  return {
    success: false,
    error: sanitizeApiNote(data?.message || data?.error, fallback),
  };
}

export type CloseAllResultRow = {
  success?: boolean;
  queued?: boolean;
  duplicate?: boolean;
  pair?: string;
  error?: string;
};

export function interpretCloseAllApiResponse(
  ok: boolean,
  status: number,
  data: {
    success?: boolean;
    partial?: boolean;
    error?: string;
    message?: string;
    results?: CloseAllResultRow[];
  } | null
  | undefined,
): { success: boolean; error?: string; message?: string; unauthorized?: boolean } {
  if (status === 401 || status === 403) {
    return {
      success: false,
      unauthorized: status === 401,
      error: data?.error || data?.message || `HTTP ${status}`,
    };
  }

  if (isNetworkStatus(status)) {
    return {
      success: false,
      error: 'Network error — failed to close all positions',
    };
  }

  const results = Array.isArray(data?.results) ? data.results : [];
  const failed = results.filter(
    (r) =>
      r &&
      (r.success === false ||
        r.queued === true ||
        r.duplicate === true ||
        isTradeResponseQueued(r)),
  );

  if (!ok || data?.success !== true || failed.length > 0) {
    const detail = failed
      .map((f) => `${f.pair || '?'}: ${f.error || (f.queued ? 'queued' : 'failed')}`)
      .join('; ');
    return {
      success: false,
      error: detail || data?.error || data?.message || 'Failed to close all positions',
    };
  }

  return { success: true, message: data?.message || 'All positions closed' };
}

export function interpretCancelApiResponse(
  ok: boolean,
  status: number,
  data: {
    success?: boolean;
    queued?: boolean;
    duplicate?: boolean;
    error?: string;
    message?: string;
    cancelled?: number;
  } | null
  | undefined,
): { success: boolean; error?: string; unauthorized?: boolean; cancelled?: number } {
  if (status === 401 || status === 403) {
    return {
      success: false,
      unauthorized: status === 401,
      error: data?.error || data?.message || `HTTP ${status}`,
    };
  }

  const queued = isTradeResponseQueued(data);
  if (ok && data?.success === true && !queued) {
    return { success: true, cancelled: Number(data?.cancelled) || 0 };
  }

  if (isNetworkStatus(status)) {
    return {
      success: false,
      error: 'Network error — failed to cancel order',
    };
  }

  const fallback = queued
    ? 'Cancel queued — order(s) not cancelled yet'
    : 'Failed to cancel order';
  return {
    success: false,
    error: sanitizeApiNote(data?.message || data?.error, fallback),
  };
}

export type OroMutateInterpretOpts = {
  /** When true (default), queued/duplicate ack is not success. Set false for subscribe-style mutates. */
  rejectQueued?: boolean;
  /** Treat data.ok === true as success (autopilot / agent endpoints). */
  acceptOkField?: boolean;
  fallbackError: string;
  networkError?: string;
  queuedError?: string;
};

/** Fail-closed interpreter for MiniApp / terminal mutates that must return explicit success. */
export function interpretOroMutateApiResponse(
  ok: boolean,
  status: number,
  data: {
    success?: boolean;
    ok?: boolean;
    queued?: boolean;
    duplicate?: boolean;
    error?: string;
    message?: string;
    details?: { queued?: boolean; duplicate?: boolean; [key: string]: unknown } | null;
  } | null
  | undefined,
  opts: OroMutateInterpretOpts,
): { success: boolean; error?: string; unauthorized?: boolean } {
  if (status === 401 || status === 403) {
    return {
      success: false,
      unauthorized: status === 401,
      error: data?.error || data?.message || `HTTP ${status}`,
    };
  }

  if (isNetworkStatus(status)) {
    return {
      success: false,
      error: opts.networkError || 'Network error',
    };
  }

  const rejectQueued = opts.rejectQueued !== false;
  const queued = rejectQueued && isTradeResponseQueued(data);
  const succeeded =
    data?.success === true || (opts.acceptOkField === true && data?.ok === true);
  if (ok && succeeded && !queued) {
    return { success: true };
  }

  const fallback = queued
    ? opts.queuedError || `${opts.fallbackError} — queued, not done yet`
    : opts.fallbackError;
  return {
    success: false,
    error: sanitizeApiNote(data?.message || data?.error, fallback),
  };
}
